import React, { useCallback, useMemo, useState } from 'react';
import { MCButton, MCText, ToggleSwitch, View } from '../basic';
import {
  permissions,
  remCss,
  rgba,
  scssVariables as variables,
  scssVariables,
  userFullName
} from 'src/helpers';
import LANG_CONST from 'src/helpers/lang/const';
import moment from 'moment';
import ShortInfo from 'components/user/ShortInfo';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import { Toggle } from './utils';
import mainModel, { STEPS } from './model';
import { EditSlot } from 'components/slotSchedule/EditSlot';
import ButtonDropdown from 'components/complex/ButtonDropdown';
import AlertManager from 'components/alert/AlertManager';
import appointmentModel from 'src/pages/main/Appointment/model';
import constants from 'src/constants';

const virtualColor = scssVariables['colors-mc-primary'];
const officeColor = scssVariables['colors-mc-orange'];

export const SlotItem = ({
  slot,
  slots,
  openEditId,
  setOpenEditId,
  onDeleted,
  onToggled,
  onSaved,
  onHide,
  openButtonId,
  setOpenButtonId,
  setScrollItemId,
  onCancelled,
  onRescheduled,
  onBooked
}) => {
  const hospitalParams = mainModel.getHospitalParams();
  const doctorId = mainModel.state.doctorId;
  const { id, deleted } = slot;
  let types = slot.patientId ? [slot.appointmentType] : slot.appointmentTypes;
  const [invalid, setInvalid] = useState({});
  const [loading, setLoading] = useState(false);

  const onDelete = useCallback(
    (slot) => {
      if (loading) return;
      setInvalid({});
      mainModel.removeSlot(slot, doctorId, (err, data) => {
        onDeleted(err, data);
      });
    },
    [loading, doctorId, onDeleted]
  );

  const toggleSlot = useCallback(
    (slot, value, cb) => {
      if (loading || !mainModel.checkModifiedPermission(slot)) return;
      setLoading(true);
      setInvalid({});

      mainModel.saveSlot(
        {
          slotId: slot.id,
          doctorId,
          key: 'enabled',
          value
        },
        (error, data) => {
          if (error) {
            setInvalid({ error });
          } else {
            if (!value) {
              setOpenEditId(null);
            }
          }
          onToggled(error, data);
          if (cb) cb(error);
          setLoading(false);
        }
      );
    },
    [doctorId, loading, setOpenEditId, onToggled]
  );

  const focusTime = useMemo(() => {
    return moment(slot.focusTime + 1000).isSameOrAfter(moment());
  }, [slot.focusTime]);

  if (deleted)
    return (
      <div className={'mc-flex-20-col mc-p10'} key={id}>
        {invalid?.error && <MCText label={invalid.error} />}
        <div className={'mc-grow mc-flex-20'}>
          <ToggleSwitch
            checked={false}
            onChange={() => {}}
            disabled={true}
            className={'disabled'}
          />
          <div className={'mc-label-520 mc-grow'}>
            {moment(slot.startDate).format('HH:mm [to] ') + moment(slot.endDate).format('HH:mm')}
          </div>
        </div>
      </div>
    );

  return (
    <div
      className={`mc-flex-col mc-radius-5 mc-p20 ${focusTime ? 'mc-b-primary' : 'mc-b2'}`}
      key={id}
    >
      <View style={{ flexDirection: 'row', gap: remCss(15) }}>
        <View style={{ flex: 1, gap: remCss(15) }}>
          <View style={{ flexDirection: 'row', gap: remCss(15) }}>
            <Toggle slot={slot} toggleSlot={toggleSlot} disabled={!mainModel.canModify(slot)} />
            {types.map((type) => (
              <View key={type}>
                <MCText
                  heading
                  label={LANG_CONST.constants.appointment_types[type]}
                  numberOfLines={1}
                  style={{
                    backgroundColor:
                      type === 'virtual' ? rgba(virtualColor, 0.3) : rgba(officeColor, 0.2),
                    paddingHorizontal: 8,
                    borderRadius: 8
                  }}
                  size={14}
                  lineHeight={20}
                  weight={400}
                />
              </View>
            ))}
          </View>
          {!!slot.hospital && types.includes('in_office') && (
            <MCText size={16} lineHeight={16} numberOfLines={1} textLight>
              {slot.hospital?.split('_')[0]}
            </MCText>
          )}
        </View>
        {!slot.appointmentId && (
          <ButtonsOfFree
            slot={slot}
            setOpenButtonId={setOpenButtonId}
            setScrollItemId={setScrollItemId}
            setOpenEditId={setOpenEditId}
            onDelete={onDelete}
            onBooked={onBooked}
            hide={onHide}
          />
        )}
      </View>
      {!!slot.appointmentId && (
        <div className={'mc-grow mc-flex-20 mc-pointer'}>
          <div className={'mc-grow mc-pl20'}>
            <ShortInfo
              avatar={slot.avatar}
              fullname={userFullName(slot)}
              description={[
                slot.email,
                slot.phoneNumber ? formatPhoneNumberIntl(slot.phoneNumber.toString()) : null
              ]
                .filter((c) => !!c)
                .join('/')}
            />
          </div>
          <ButtonsOfBooked
            slot={slot}
            setOpenButtonId={setOpenButtonId}
            setScrollItemId={setScrollItemId}
            doctor={doctorId}
            hide={onHide}
            onRescheduled={onRescheduled}
            onCancelled={onCancelled}
          />
        </div>
      )}
      {openEditId === id && (
        <EditSlot
          slot={slot}
          onSaved={onSaved}
          doctorId={doctorId}
          slots={slots}
          hospitalParams={hospitalParams}
          onClose={() => setOpenEditId(null)}
        />
      )}
      {openEditId !== id && id === openButtonId && (
        <View style={{ marginBottom: remCss(slot.appointmentId ? 23 : 66) }} />
      )}
    </div>
  );
};

const ButtonsOfBooked = ({
  slot,
  hide,
  setOpenButtonId,
  setScrollItemId,
  onRescheduled,
  onCancelled
}) => {
  if (!mainModel.canModify()) return null;

  const params = {
    auth: mainModel.state.user,
    appointment: slot.appointment
  };

  return (
    <div className={'mc-flex-20'} style={{ justifyContent: 'flex-end' }}>
      {permissions.ACTIONS.APPOINTMENTS_RESCHEDULE_BUTTON.checkActive(params) && (
        <MCButton
          {...permissions.ACTIONS.APPOINTMENTS_RESCHEDULE_BUTTON.attributes}
          medium
          hover
          outline
          radius
          onClick={() => {
            mainModel.setNextStep(STEPS.RESCHEDULE, {
              editId: slot.id,
              editAppointmentId: slot.appointmentId,
              selectedDay: slot.startDate,
              type: slot.appointmentType,
              onDone: () => {
                mainModel.backStep();
                onRescheduled();
              }
            });
          }}
        />
      )}
      {permissions.ACTIONS.APPOINTMENTS_REBOOK_BUTTON.checkActive(params) && (
        <MCButton
          {...permissions.ACTIONS.APPOINTMENTS_REBOOK_BUTTON.attributes}
          medium
          hover
          outline
          radius
          onClick={() => {
            hide();
            mainModel.emit('router_navigate', {
              url: permissions.PAGES.ADMIN_APPOINTMENTS_REBOOK_PAGE.match(slot.appointmentId)
            });
          }}
        />
      )}
      {permissions.ACTIONS.APPOINTMENTS_CANCEL_BUTTON.checkActive(params) && (
        <View>
          <ButtonDropdown
            onClose={(oldState) => {
              setOpenButtonId(oldState ? null : slot.id);
              if (!oldState) setScrollItemId(slot.id);
            }}
            triggerBoundsWidth={false}
            iconProps={{ style: {} }}
            triggerOffset={2}
            popupMenuStyle={{
              borderTop: `1px solid ${rgba(variables['colors-mc-primary'])}`
            }}
            menu={(toggle) => (
              <View style={{ width: remCss(89) }}>
                <MCButton
                  {...permissions.ACTIONS.APPOINTMENTS_CANCEL_BUTTON.attributes}
                  medium
                  style={{ width: '100%' }}
                  hover
                  radius={false}
                  onClick={() => {
                    appointmentModel.showCancel(slot.appointment, onCancelled);
                    toggle();
                  }}
                />
              </View>
            )}
            mainButton={() => null}
          />
        </View>
      )}
    </div>
  );
};

const ButtonsOfFree = ({
  slot,
  setOpenButtonId,
  setScrollItemId,
  setOpenEditId,
  onDelete,
  onBooked
}) => {
  const focusTime = useMemo(() => {
    return moment(slot.startDate).isSameOrAfter(moment());
  }, [slot.startDate]);

  if (!mainModel.canModify()) return null;

  return (
    <div className={'mc-flex-20'} style={{ justifyContent: 'flex-end' }}>
      {focusTime && (
        <MCButton
          medium
          hover
          outline
          light
          radius
          label={LANG_CONST.buttons.book}
          onClick={() => {
            if (mainModel.state.user?.status === constants.general.userStatus.disabled) {
              return mainModel.emit('checkDisabledAccount', mainModel.state.user);
            }
            if (
              !mainModel.state.user?.isDoctor &&
              !permissions.PAGES.APPOINTMENTS_CREATE_PAGE.isActive
            ) {
              return AlertManager.popup(AlertManager.TYPES.INFO, {
                width: remCss(650),
                title: LANG_CONST.manager_no_permissions.appointments,
                actionButton: {
                  label: LANG_CONST.buttons.ok,
                  action: ({ hide }) => hide()
                }
              });
            }
            mainModel.setNextStep(STEPS.BOOK, {
              slot,
              onDone: () => {
                mainModel.backStep();
                onBooked();
              }
            });
          }}
        />
      )}
      <View>
        <ButtonDropdown
          onClose={(oldState) => {
            setOpenButtonId(oldState ? null : slot.id);
            if (!oldState) setScrollItemId(slot.id);
          }}
          triggerBoundsWidth={false}
          iconProps={{ style: {} }}
          triggerOffset={2}
          popupMenuStyle={{
            borderTop: `1px solid ${rgba(variables['colors-mc-primary'])}`
          }}
          menu={(toggle) => (
            <View style={{ width: remCss(89) }}>
              <MCButton
                medium
                light
                style={{ width: '100%' }}
                hover
                radius={false}
                label={LANG_CONST.buttons.edit}
                onClick={() => {
                  setOpenEditId((o) => (o !== slot.id ? slot.id : null));
                  setScrollItemId(slot.id);
                  toggle();
                }}
              />
              <MCButton
                quaternary
                light
                style={{ width: '100%' }}
                hover
                radius={false}
                label={LANG_CONST.buttons.delete}
                onClick={() => {
                  onDelete(slot);
                  toggle();
                }}
              />
            </View>
          )}
          mainButton={() => null}
        />
      </View>
    </div>
  );
};
